<template>
	<v-card style="width: 100%; height: 100%; min-height: 250px">
		<v-card-title class="py-2 py-lg-4">
			<v-row no-gutters>
				<v-col cols="6" class="text-left" :class="$vuetify.breakpoint.mdAndDown ? 'font-responsive-md' : ''">
					{{ $t('diagnoses') }}
				</v-col>
				<v-spacer />
				<v-col cols="2" class="text-right">
					<ActionButton
						icon="mdi-file-delimited"
						tooltip="download-csv"
						:maxSize="25"
						:minSize="20"
						@clicked="downloadCSV()"
						:busy="downloading" />
					<v-icon
						color="primary"
						@click="synchronizeAddDrawers('ccmDiagnosisAdd')"
						:size="$vuetify.breakpoint.mdAndDown ? 20 : 25">
						mdi-plus-circle
					</v-icon>
				</v-col>
			</v-row>
		</v-card-title>
		<v-card-text :style="$vuetify.breakpoint.mdAndDown ? 'height: calc(100% - 48px)' : 'height: calc(100% - 64px)'">
			<v-data-table
				dense
				style="height: 100%"
				:height="'calc(100% - 59px)'"
				:headers="diagnosisHeaders"
				fixed-header
				:no-data-text="$t('no-data-available')"
				:items="diagnoses"
				:items-per-page="-1"
				class="overflow-y-auto data-table-outline"
				:footer-props="{ 'items-per-page-text': '' }">
				<template v-slot:item="{ item, index }">
					<tr class="cursor" :class="getRowsForDataTable(index)" @click.stop="openViewDialog(item)">
						<td class="text-left px-1">
							{{ item.diagnosisCode }}
						</td>
						<td class="text-left px-1 truncate-text" style="max-width: 15vw">
							{{ item.diagnoseDescription }}
						</td>
						<td class="text-left px-1">
							{{ formatDuration(item.timeSpentSeconds) }}
						</td>
						<td class="px-1 text-right">
							<ActionButton
								v-if="item.ccmRecordId === currentRecord.id"
								icon="mdi-delete"
								tooltip="delete"
								:maxSize="25"
								:minSize="20"
								@clicked="
									deleteId = item.id;
									showDeleteDialog = true;
								"
								:busy="deletingIds.includes(item.id)" />
						</td>
					</tr>
				</template>
				<template v-slot:[`footer.prepend`]>
					<v-row dense class="ma-0">
						<v-col class="text-left">
							<p class="ma-0">
								<v-tooltip top>
									<template v-slot:activator="{ on }">
										<v-icon class="ml-1" color="primary" size="25" v-on="on"> mdi-help-circle-outline </v-icon>
									</template>
									{{ $t('need-to-select-the-correct-record') }}
								</v-tooltip>
							</p>
						</v-col>
					</v-row>
				</template>
			</v-data-table>
		</v-card-text>
		<CCMDiagnosisViewDialog
			:showDialog="showViewDialog"
			v-if="showViewDialog && selectedDiagnosis"
			:selectedDiagnosis="selectedDiagnosis"
			@closeDialog="showViewDialog = false" />

		<DeleteDialog
			:dialog.sync="showDeleteDialog"
			@confirm="
				showDeleteDialog = false;
				deletingIds.push(deleteId);
				deleteDiagnosis(deleteId);
				deleteId = -1;
			" />
	</v-card>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import { formatDuration, downloadCSVWithKeys, getFullName, getRowsForDataTable } from '../../../../utils/utilities';
import DeleteDialog from '@/views/dialogs/DeleteDialog.vue';
import CCMDiagnosisViewDialog from '../../../dialogs/CCMDiagnosisViewDialog.vue';
import ActionButton from '../ActionButton.vue';

export default {
	name: 'CCMDiagnosis',
	components: { DeleteDialog, CCMDiagnosisViewDialog, ActionButton },
	data () {
		return {
			deleteId: -1,
			deletingIds: [],
			selectedDiagnosis: null,
			showDeleteDialog: false,
			showViewDialog: false,
			downloading: false,
		};
	},

	computed: {
		...mapState({
			currentRecord: (state) => state.ccmRecords.ccmRecord,
			diagnoses: (state) => state.ccmDiagnoses.ccmDiagnosesByPatientId,
			detailedUser: (state) => state.users.detailedUser,
		}),
		diagnosisHeaders () {
			return [
				{
					text: 'ICD-10',
					sortable: false,
					value: 'diagnosisCode',
					class: 'px-1',
				},
				{
					text: this.$t('description'),
					sortable: false,
					value: 'diagnoseDescription',
					class: 'px-1',
				},
				{
					text: this.$t('time-spent-header'),
					sortable: false,
					value: 'timeSpentSeconds',
					class: 'px-1',
				},
				{
					text: this.$t('actions'),
					sortable: false,
					value: '',
					align: 'right',
					class: 'pl-1 pr-3',
				},
			];
		},
	},

	methods: {
		...mapActions({
			deleteCCMDiagnosis: 'ccmDiagnoses/deleteCCMDiagnosis',
			getSelectedCCMRecordId: 'ccmRecords/getSelectedCCMRecordId',
			getCCMDiagnosesByPatientId: 'ccmDiagnoses/getCCMDiagnosesByPatientId',
		}),
		formatDuration,
		getRowsForDataTable,
		downloadCSVWithKeys,
		getFullName,
		async deleteDiagnosis (id) {
			await this.deleteCCMDiagnosis(id);
			this.deletingIds = this.deletingIds.filter((item) => item !== id);
		},
		openViewDialog (diagnosis) {
			this.selectedDiagnosis = diagnosis;
			this.showViewDialog = true;
		},
		synchronizeAddDrawers (drawer) {
			const parameters = {
				detailsDrawer: true,
				page: drawer,
			};
			this.$store.commit('SET_DETAILS_DRAWER', parameters);
		},
		async downloadCSV () {
			this.downloading = true;
			await downloadCSVWithKeys(this.diagnoses, 'Diagnoses_for_' + getFullName(this.detailedUser.userData), {
				ids: ['ccmRecordId'],
				data: ['diagnosisCode', 'diagnosisType', 'diagnoseDescription', 'doctorName'],
				timestamps: ['diagnoseTimestampUTC'],
				timeSpent: ['timeSpentSeconds'],
			});
			this.downloading = false;
		},
	},

	async beforeMount () {
		this.$store.commit('ccmDiagnoses/STORE_CCM_DIAGNOSES_BY_PATIENT_ID', []);
		await this.getCCMDiagnosesByPatientId(this.detailedUser.userData.id);
	},
};
</script>
